import { useEffect, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useStore } from '../store';
import { useFocusTrap } from '../utils/focusTrap';
import { waitForAck } from '../utils/waitForAck';
import { send, waitForReady } from '../ws';
import styles from './CreatePublishableTokenDialog.module.css';

interface KeyRecord {
  kid: string;
  isAnonymous: boolean;
  name?: string | null;
  description?: string | null;
  token?: string | null;
  createdAt: string;
  updatedAt: string;
}

interface CreatePublishableTokenDialogProps {
  projectId: string;
  onClose: () => void;
  onCreated: (record: KeyRecord) => void;
}

const EXPIRY_PRESETS: { label: string; seconds: number | null }[] = [
  { label: 'Never', seconds: null },
  { label: '1 hour', seconds: 3600 },
  { label: '1 day', seconds: 86400 },
  { label: '7 days', seconds: 604800 },
  { label: '30 days', seconds: 2592000 },
  { label: '1 year', seconds: 31536000 },
  { label: 'Custom', seconds: -1 },
];

const CUSTOM_UNITS: { label: string; multiplier: number }[] = [
  { label: 'minutes', multiplier: 60 },
  { label: 'hours', multiplier: 3600 },
  { label: 'days', multiplier: 86400 },
];

export function CreatePublishableTokenDialog({
  projectId,
  onClose,
  onCreated,
}: CreatePublishableTokenDialogProps) {
  const modalRef = useRef<HTMLDivElement>(null);

  // Required fields
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  // Optional claim fields
  const [applicationId, setApplicationId] = useState('');
  const [versionId, setVersionId] = useState('');
  const [expiryPreset, setExpiryPreset] = useState<number | null>(null); // null = never
  const [customAmount, setCustomAmount] = useState('');
  const [customUnit, setCustomUnit] = useState(3600); // hours
  const [reference, setReference] = useState('');
  const [swiftJobRequest, setSwiftJobRequest] = useState(false);

  // UI state
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState('');

  // Store data
  const applications = useStore(s => s.applications);
  const versions = useStore(s => s.versions);

  const projectApps = Object.values(applications).filter(a => a.projectId === projectId);
  const appVersions = applicationId
    ? Object.values(versions).filter(v => v.appId === applicationId)
    : [];

  useFocusTrap(modalRef, true);

  // Store onClose in ref for escape handler
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onCloseRef.current();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Reset version when application changes
  useEffect(() => {
    setVersionId('');
  }, [applicationId]);

  const isCustomExpiry = expiryPreset === -1;

  const computeExpiresInSeconds = (): number | undefined => {
    if (expiryPreset === null) return undefined; // "Never" = server default (10 years)
    if (isCustomExpiry) {
      const amount = parseInt(customAmount, 10);
      if (!amount || amount <= 0) return undefined;
      return amount * customUnit;
    }
    return expiryPreset;
  };

  const handleCreate = async () => {
    if (!name.trim() || !description.trim()) return;

    setIsCreating(true);
    setError('');

    try {
      await waitForReady();
      const corr = uuidv4();

      const payload: Parameters<typeof send<'project.anonymous_jwks.generate'>>[1] = {
        projectId,
        name: name.trim(),
        description: description.trim(),
      };

      if (applicationId) payload.applicationId = applicationId;
      if (applicationId && versionId) payload.versionId = versionId;
      if (reference.trim()) payload.reference = reference.trim();
      if (swiftJobRequest) payload.swiftJobRequest = true;

      const expiresInSeconds = computeExpiresInSeconds();
      if (expiresInSeconds !== undefined) payload.expiresInSeconds = expiresInSeconds;

      send('project.anonymous_jwks.generate', payload, corr);

      const result = await waitForAck<{
        ok: boolean;
        kid: string;
        token: string;
        name: string;
        description: string;
        createdAt: string;
      }>(corr);

      onCreated({
        kid: result.kid,
        isAnonymous: true,
        name: result.name,
        description: result.description,
        token: result.token,
        createdAt: result.createdAt,
        updatedAt: result.createdAt,
      });
    } catch (err: any) {
      setError(err?.message || 'Failed to create publishable token');
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <div
      className={styles.modalOverlay}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="create-token-title"
    >
      <div ref={modalRef} className={styles.modal} onClick={e => e.stopPropagation()}>
        <h2 id="create-token-title" className={styles.modalTitle}>
          Create Publishable Token
        </h2>

        {error && <div className={styles.error}>{error}</div>}

        {/* Required fields */}
        <div className={styles.formGroup}>
          <label htmlFor="token-name" className={styles.label}>Name</label>
          <input
            id="token-name"
            type="text"
            className={styles.input}
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder="e.g. Production website"
            autoFocus
          />
        </div>

        <div className={styles.formGroup}>
          <label htmlFor="token-desc" className={styles.label}>Description</label>
          <input
            id="token-desc"
            type="text"
            className={styles.input}
            value={description}
            onChange={e => setDescription(e.target.value)}
            placeholder="e.g. Embedded in https://myapp.com frontend"
          />
        </div>

        <hr className={styles.divider} />
        <div className={styles.sectionLabel}>Claims</div>

        {/* Application */}
        <div className={styles.formGroup}>
          <label htmlFor="token-app" className={styles.label}>Application</label>
          <select
            id="token-app"
            className={styles.select}
            value={applicationId}
            onChange={e => setApplicationId(e.target.value)}
          >
            <option value="">Any</option>
            {projectApps.map(app => (
              <option key={app.id} value={app.id}>{app.name}</option>
            ))}
          </select>
          <div className={styles.hint}>Restrict token to a specific application</div>
        </div>

        {/* Version - only shown when an application is selected */}
        {applicationId && (
          <div className={styles.formGroup}>
            <label htmlFor="token-version" className={styles.label}>Version</label>
            <select
              id="token-version"
              className={styles.select}
              value={versionId}
              onChange={e => setVersionId(e.target.value)}
            >
              <option value="">Default</option>
              {appVersions.map(v => (
                <option key={v.id} value={v.id}>{v.id}</option>
              ))}
            </select>
          </div>
        )}

        {/* Expiry */}
        <div className={styles.formGroup}>
          <label htmlFor="token-expiry" className={styles.label}>Expires</label>
          <select
            id="token-expiry"
            className={styles.select}
            value={expiryPreset === null ? '' : String(expiryPreset)}
            onChange={e => {
              const val = e.target.value;
              setExpiryPreset(val === '' ? null : Number(val));
            }}
          >
            {EXPIRY_PRESETS.map(p => (
              <option key={p.label} value={p.seconds === null ? '' : String(p.seconds)}>
                {p.label}
              </option>
            ))}
          </select>
          {isCustomExpiry && (
            <div className={styles.expiryCustomRow}>
              <input
                type="number"
                className={styles.input}
                value={customAmount}
                onChange={e => setCustomAmount(e.target.value)}
                placeholder="Amount"
                min="1"
              />
              <select
                className={styles.select}
                value={customUnit}
                onChange={e => setCustomUnit(Number(e.target.value))}
              >
                {CUSTOM_UNITS.map(u => (
                  <option key={u.label} value={u.multiplier}>{u.label}</option>
                ))}
              </select>
            </div>
          )}
        </div>

        {/* Reference */}
        <div className={styles.formGroup}>
          <label htmlFor="token-ref" className={styles.label}>Reference</label>
          <input
            id="token-ref"
            type="text"
            className={styles.input}
            value={reference}
            onChange={e => setReference(e.target.value)}
            placeholder="e.g. campaign-2024-q1"
          />
          <div className={styles.hint}>Opaque tracking identifier, must be unique within project</div>
        </div>

        {/* Swift Job Request */}
        <div className={styles.formGroup}>
          <label className={styles.checkboxRow}>
            <input
              type="checkbox"
              className={styles.checkbox}
              checked={swiftJobRequest}
              onChange={e => setSwiftJobRequest(e.target.checked)}
            />
            <span className={styles.checkboxLabel}>Swift Job Request</span>
          </label>
          <div className={styles.hint}>High intent signal — submit job during token exchange</div>
        </div>

        {/* Actions */}
        <div className={styles.buttonGroup}>
          <button
            type="button"
            onClick={onClose}
            className={`${styles.button} ${styles.buttonSecondary}`}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleCreate}
            disabled={isCreating || !name.trim() || !description.trim()}
            className={styles.button}
          >
            {isCreating ? 'Creating\u2026' : 'Create'}
          </button>
        </div>
      </div>
    </div>
  );
}
